import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { BackupVmMapping } from '../types/models/BackupVmMapping';
import type { ApiResponseBackupVmMappingList } from '../types/api/ApiResponseBackupVmMapping';

/** Server-side scope of the overrides list — every field maps to a query parameter. */
export interface BackupVmMappingFilters {
  /** Scope to one directory mapping. `null` = every directory. */
  backupMappingId: number | null;
  /** Overrides attributing to this client. `null` = every client. */
  clientID: number | null;
  /** `orphans=1`: only rows whose triple matches no reported subject — the typo/decommission queue. */
  orphansOnly: boolean;
}

interface UseBackupVmMappingsReturn {
  mappings: BackupVmMapping[];
  /**
   * Orphans across the WHOLE table, ignoring the filters above — a badge for the tab, not a count
   * of the rows returned. Seeded from the server and nudged by local mutations so the badge stays
   * honest between refetches.
   */
  orphanCount: number;
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Upsert one row from a create/update response (matched by `id`), so a save updates its row
   * without a full refetch (which would flash the loading spinner over the table). A row that
   * isn't in the list yet (a fresh create) is appended — the backend orders by id, so appending
   * the newest row preserves that order.
   *
   * `previous` is the row as it was before the edit (null on create). It is required because the
   * orphan delta depends on both sides: only the caller knows which row was replaced, and reading
   * it out of the list inside a state updater would make the updater impure.
   */
  applyMapping: (updated: BackupVmMapping, previous: BackupVmMapping | null) => void;
  /** Drop one row after a successful DELETE. Takes the row (not the id) to adjust the orphan badge. */
  removeMapping: (removed: BackupVmMapping) => void;
}

const buildQuery = (filters: BackupVmMappingFilters): string => {
  const params = new URLSearchParams();
  if (filters.backupMappingId !== null) {
    params.set('backup_mapping_id', String(filters.backupMappingId));
  }
  if (filters.clientID !== null) {
    params.set('clientID', String(filters.clientID));
  }
  if (filters.orphansOnly) {
    params.set('orphans', '1');
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
};

/** An override with no matching reported subject is what `orphan_count` counts. */
const orphanWeight = (mapping: BackupVmMapping): number => (mapping.subject_exists ? 0 : 1);

/**
 * GET /admin/backup-vm-mappings — the per-VM attribution decisions already made, across all
 * tenants. Deliberately NOT paginated by the backend: this table holds one row per deliberate
 * staff decision, not one per VM.
 *
 * Staff-only on the backend; the page mirrors the gate, and `enabled` defers the fetch for a
 * non-staff visitor exactly as in `useBackupAttributionQueue`.
 *
 * `applyMapping`/`removeMapping` keep the list in step with a mutation without a refetch; the page
 * additionally refetches when the queue tab settles a VM, since that creates a row here.
 */
export const useBackupVmMappings = (enabled = true, filters: BackupVmMappingFilters): UseBackupVmMappingsReturn => {
  const [mappings, setMappings] = useState<BackupVmMapping[]>([]);
  const [orphanCount, setOrphanCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const { backupMappingId, clientID, orphansOnly } = filters;

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `/admin/backup-vm-mappings${buildQuery({ backupMappingId, clientID, orphansOnly })}`,
      );
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseBackupVmMappingList | null;
      if (body?.status === 'success' && body.data?.backup_vm_mappings) {
        setMappings(body.data.backup_vm_mappings);
        setOrphanCount(body.data.orphan_count ?? 0);
      } else {
        setError(body?.message ?? 'Failed to load backup attributions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching backup attributions');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, backupMappingId, clientID, orphansOnly]);

  const applyMapping = useCallback((updated: BackupVmMapping, previous: BackupVmMapping | null) => {
    setMappings((prev) =>
      prev.some((m) => m.id === updated.id) ? prev.map((m) => (m.id === updated.id ? updated : m)) : [...prev, updated],
    );
    const delta = orphanWeight(updated) - (previous === null ? 0 : orphanWeight(previous));
    if (delta !== 0) {
      setOrphanCount((prev) => Math.max(0, prev + delta));
    }
  }, []);

  const removeMapping = useCallback((removed: BackupVmMapping) => {
    setMappings((prev) => prev.filter((m) => m.id !== removed.id));
    if (!removed.subject_exists) {
      setOrphanCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchMappings();
    }
  }, [enabled, auth.isAuthenticated, fetchMappings]);

  return { mappings, orphanCount, loading, error, refetch: fetchMappings, applyMapping, removeMapping };
};
